import React, { useState } from 'react';
import styles from './ModulesContentSidebar.module.css';
import { Module } from '../../types/course.types';
import { useNavigate, useParams } from 'react-router-dom';
import { Lesson } from '../hooks/useModuleLessons';

import { tr, trn } from '@/i18n';
interface ModulesContentSidebarProps {
  modules: Module[];
  getLessons?: (moduleId: string) => Lesson[];
  onClose?: () => void;
  onAddSeminar?: () => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onTitleChange?: (id: string, title: string) => void;
  showActions?: boolean;
}

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0.781384 16C0.626848 16 0.475776 15.9542 0.347275 15.8684C0.218774 15.7825 0.118619 15.6605 0.0594767 15.5178C0.000334656 15.375 -0.0151367 15.2179 0.0150196 15.0663C0.0451759 14.9147 0.119605 14.7755 0.228892 14.6663L14.6662 0.228859C14.8127 0.082328 15.0114 7.62939e-06 15.2187 7.62939e-06C15.4259 7.62939e-06 15.6246 0.082328 15.7711 0.228859C15.9177 0.37539 16 0.574129 16 0.781356C16 0.988582 15.9177 1.18732 15.7711 1.33385L1.33388 15.7713C1.26139 15.8439 1.17526 15.9015 1.08045 15.9408C0.985637 15.98 0.884002 16.0001 0.781384 16Z" fill="#9A99A7" />
    <path d="M15.2186 16C15.116 16.0001 15.0144 15.98 14.9195 15.9407C14.8247 15.9015 14.7386 15.8439 14.6661 15.7713L0.228849 1.33384C0.0823196 1.18731 0 0.988574 0 0.781348C0 0.574122 0.0823196 0.375383 0.228849 0.228852C0.375379 0.0823204 0.574116 0 0.781341 0C0.988565 0 1.1873 0.0823204 1.33383 0.228852L15.7711 14.6663C15.8804 14.7755 15.9548 14.9147 15.985 15.0663C16.0151 15.2179 15.9997 15.375 15.9405 15.5177C15.8814 15.6605 15.7812 15.7825 15.6527 15.8684C15.5242 15.9542 15.3732 16 15.2186 16Z" fill="#9A99A7" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M11.7679 9.28965C11.8593 9.38261 11.9319 9.49321 11.9815 9.61506C12.031 9.73691 12.0565 9.86761 12.0565 9.99961C12.0565 10.1316 12.031 10.2623 11.9815 10.3842C11.9319 10.506 11.8593 10.6166 11.7679 10.7096L7.28866 15.2893C7.19719 15.3823 7.12459 15.4929 7.07505 15.6147C7.02551 15.7366 7 15.8673 7 15.9993C7 16.1313 7.02551 16.262 7.07505 16.3838C7.12459 16.5057 7.19719 16.6163 7.28866 16.7092C7.4715 16.8955 7.71883 17 7.97664 17C8.23445 17 8.48178 16.8955 8.66462 16.7092L13.1438 12.1195C13.6921 11.557 14 10.7946 14 9.99961C14 9.20466 13.6921 8.4422 13.1438 7.87974L8.66462 3.29C8.48286 3.10527 8.23753 3.00112 7.98152 3.00002C7.85309 2.99926 7.72577 3.02448 7.60688 3.07424C7.48798 3.124 7.37984 3.19733 7.28866 3.29C7.19719 3.38296 7.12459 3.49355 7.07505 3.6154C7.02551 3.73726 7 3.86796 7 3.99996C7 4.13196 7.02551 4.26266 7.07505 4.38451C7.12459 4.50637 7.19719 4.61696 7.28866 4.70992L11.7679 9.28965Z" fill="#9A99A7" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="5" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

const ModuleDropdown: React.FC<{
  onDuplicate: () => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ onDuplicate, onDelete, onClose }) => (
  <div className={styles.dropdown}>
    <button className={styles.dropdownItem} onClick={() => { onDuplicate(); onClose(); }}>
      <CopyIcon /> {tr("ui.duplicate", "Duplicate")}
    </button>
    <button className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`} onClick={() => { onDelete(); onClose(); }}>
      <TrashIcon /> {tr('common.delete', 'Delete')}
    </button>
  </div>
);

const ModuleItem: React.FC<{
  moduleIndex: number;
  module: Module;
  lessons: Lesson[];
  defaultOpen: boolean;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  showActions: boolean;
}> = ({ moduleIndex, module, lessons, defaultOpen, onDelete, onDuplicate, showActions }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [openLessons, setOpenLessons] = useState<string[]>(
    defaultOpen && lessons.length > 0 ? [lessons[0].id] : []
  );
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleLesson = (id: string) => {
    setOpenLessons(prev => prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]);
  };

  return (
    <div className={`${styles.moduleCard} ${open ? styles.open : ''}`}>
      <div className={styles.moduleHeader} onClick={() => setOpen(p => !p)}>
        <div className={styles.chevronIcon}><ChevronIcon /></div>

        <div className={styles.moduleTopRow}>
          <span className={`${styles.moduleInfo} subtitle-7`}>{tr("ui.module_1cf5e1", "MODULE")} {moduleIndex + 1}</span>
          <h3 className={`${styles.moduleTitle} h4`}>{module.title || tr("ui.untitled_module", "Untitled module")}</h3>
          <div className={styles.moduleProgressRow}>
            <div className={styles.miniProgressTrack}>
              <div className={styles.miniProgressFill} style={{ width: '0%' }} />
            </div>
            <span className={`${styles.miniProgressText} subtitle-2`}>0%</span>
          </div>
          <p className={`${styles.moduleDescription} subtitle-3`}>{tr("ui.no_description_yet", "No description yet")}</p>
        </div>

        {showActions && onDelete && onDuplicate && (
          <div className={styles.menuWrapper} onClick={e => e.stopPropagation()}>
            <button
              className={`${styles.iconBtn} ${menuOpen ? styles.iconBtnActive : ''}`}
              onClick={() => setMenuOpen(p => !p)}
            >
              <DotsIcon />
            </button>
            {menuOpen && (
              <ModuleDropdown
                onDuplicate={() => { onDuplicate(module.id); setMenuOpen(false); }}
                onDelete={() => { onDelete(module.id); setMenuOpen(false); }}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </div>
        )}
      </div>

      <div className={`${styles.moduleContentWrapper} ${open ? styles.open : ''}`}>
        <div className={styles.moduleContentInner}>
          {lessons.length === 0 ? (
            <p className={`${styles.emptyLessons} subtitle-2`}>{tr("ui.no_lessons_yet", "No lessons yet")}</p>
          ) : (
            lessons.map(lesson => {
              const isOpen = openLessons.includes(lesson.id);
              return (
                <div key={lesson.id} className={styles.lessonGroup}>
                  <div
                    className={`${styles.lessonHeader} subtitle-3`}
                    onClick={() => toggleLesson(lesson.id)}
                  >
                    <span>{lesson.title || tr("ui.untitled_lesson", "Untitled lesson")}</span>
                    <div className={`${styles.chevronSmall} ${isOpen ? styles.open : ''}`}>
                      <ChevronIcon />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export const ModulesContentSidebar: React.FC<ModulesContentSidebarProps> = ({
  modules,
  getLessons,
  onClose,
  onAddSeminar,
  onDelete,
  onDuplicate,
  showActions = false,
}) => {
  const navigate = useNavigate();
  const { id: courseId } = useParams<{ id: string }>();

  const getL = (moduleId: string): Lesson[] => getLessons ? getLessons(moduleId) : [];

  return (
    <div className={styles.container}>
      <div className={styles.headerBlock}>
        <div className={styles.titleRow}>
          <h2 className={`${styles.title} h3`}>{tr("ui.course_content", "Course content")}</h2>
          <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        </div>
        <div className={`${styles.subtitle} subtitle-2`}>
          <span>{modules.length} {trn("ui.module", modules.length, "module", "modules")}</span>
          <span className={styles.bullet}>•</span>
          <span>{tr("ui.0_hours", "0 hours")}</span>
          <span className={styles.bullet}>•</span>
          <span>{tr("ui.certificate_of_completion", "Certificate of completion")}</span>
        </div>
      </div>

      {modules.length > 0 && (
        <div className={styles.modulesList}>
          {modules.map((m, mi) => (
            <ModuleItem
              key={m.id}
              moduleIndex={mi}
              module={m}
              lessons={getL(m.id)}
              defaultOpen={mi === 0}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              showActions={showActions}
            />
          ))}
        </div>
      )}

      {!showActions && modules.length === 0 && (
        <div className={styles.emptyState}>
          <p className={`${styles.emptyText} body-1`}>
            {tr("ui.course_content_will_appear_here", "Course content will appear here.")}{'\n'}{tr("ui.start_by_adding_modules_on_the_left", "Start by adding modules on the left.")}
          </p>
        </div>
      )}

      {showActions && (
        <div className={styles.actionsBlock}>
          {modules.length === 0 && (
            <p className={`${styles.actionsEmptyText} subtitle-2`}>
              {tr("ui.this_course_doesn_t_have_content_yet", "This course doesn't have content yet")}
            </p>
          )}
          <button
            className={`${styles.createModuleBtn} button`}
            onClick={() => navigate(`/admin/dashboard/learning/courses/build/${courseId}/modules`)}
          >
            {tr("ui.create_module", "+ Create module")}
          </button>
          <button className={`${styles.addSeminarBtn} button`} onClick={onAddSeminar}>
            {tr("ui.add_seminar", "Add seminar")}
          </button>
        </div>
      )}
    </div>
  );
};